import './StockDashboard.css';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useMemo } from 'react';

function ExpandableCard({ title, subtitle, children, actions }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="expand-card">
      <div className="expand-card-header" onClick={() => setOpen(!open)}>
        <div>
          <div className="card-title"><strong>{title}</strong></div>
          {subtitle && <div className="card-sub">{subtitle}</div>}
        </div>
        <span className="chevron">{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="expand-card-body">
          {children}
          {actions && <div className="card-actions">{actions}</div>}
        </div>
      )}
    </div>
  );
}


export default function StockDashboard(){
  const { user, token, logout } = useAuth(); 
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [inventory, setInventory] = useState([]);
  const [pendingQAs, setPendingQAs] = useState([]);
  const [qualityChecks, setQualityChecks] = useState([]);
  const [shelfMovements, setShelfMovements] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [currentTab, setCurrentTab] = useState("inventory");

  const [showModal, setShowModal] = useState(false);
  const [qaForm, setQaForm] = useState({
    receivingId: "",
    actualQuantityReceived: "",
    quantityInspected: "",
    quantityAccepted: "",
    quantityDamaged: "",
    quantityExpired: "",
    quantityDefective: "",
    quantityIncorrect: "",
    overallCondition: "",
    rejectionReasons: "",
    notes: "",
    requiresFollowUp: false
  });

  const [showShelfModal, setShowShelfModal] = useState(false);
  const [shelfForm, setShelfForm] = useState({
    productId: "",
    movementType: "",
    quantityMoved: "",
    notes: ""
  });

  const [warehouses, setWarehouses] = useState([]);
  
  const assignedWarehouseId = user.warehouseId;

  const FetchWarehouses = async () => {
    try {
      //Try to read cached warehouses
      const cachedWarehouses = sessionStorage.getItem("warehouses");

      if (cachedWarehouses) {
        // Load from cache immediately
        setWarehouses(JSON.parse(cachedWarehouses));
        return;
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      const [warehousesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/warehouse`, { headers }),
      ]);

      if (!warehousesRes.ok) {
        throw new Error("Failed to fetch warehouses");
      }

      const [warehousesData] = await Promise.all([
        warehousesRes.json(),
      ]);

      setWarehouses(warehousesData);
       

      sessionStorage.setItem("warehouses", JSON.stringify(warehousesData));
    } catch (err) {
      console.error("Error fetching warehouses:", err);
    }
  };

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        setLoading(true);
        setError("");

        await Promise.all([
          FetchWarehouses()
        ]);
        
      } catch (err) {
        console.error(err);
        setError("Failed to load data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadWarehouses();
  }, [token]);

  const getWarehouseName = (warehouseId) => {
    if (!warehouseId) return '-';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse?.name
  };

  const openModal = (receivingId) => {
    setQaForm({
      receivingId: receivingId,
      actualQuantityReceived: "",
      quantityInspected: "",
      quantityAccepted: "",
      quantityDamaged: "",
      quantityExpired: "",
      quantityDefective: "",
      quantityIncorrect: "",
      overallCondition: "",
      rejectionReasons: "",
      notes: "",
      requiresFollowUp: false
    });
    setShowModal(true);
  };

  const openShelfModal = (productId) => {
    setShelfForm({
      productId,
      quantityMoved: "",
      notes: ""
    });
    setShowShelfModal(true);
  };

  const closeModal = () => setShowModal(false);
  const closeShelfModal = () => setShowShelfModal(false);


  const loadQualityChecks = async () => {
      const response = await fetch(`${API_BASE_URL}/ShopStockReceiving/quality-checks?shopId=${`${assignedWarehouseId}&page=${page}&pageSize=${pageSize}`}`,
        {
          
          headers: {
            "Authorization": `Bearer ${token}`}
        }
      );

      if(!response.ok) throw new Error("Failed to fetch inventory");

      const data = await response.json();

      setQualityChecks(data);
  };

  const loadShelfMovements = async () => {
      const response = await fetch(`${API_BASE_URL}/ShopShelfMovement/movements?shopId=${`${assignedWarehouseId}&page=${page}&pageSize=${pageSize}`}`,
        {
          
          headers: {
            "Authorization": `Bearer ${token}`}
        }
      );

      if(!response.ok) throw new Error("Failed to fetch inventory");

      const data = await response.json();
       
      setShelfMovements(data);
  };

 useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError("");

        await Promise.all([
          fetchInventory(),
          fetchPendingQAs(),
          loadQualityChecks(),
          loadShelfMovements()
        ]);
        
      } catch (err) {
        console.error(err);
        setError("Failed to load data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [page, token, assignedWarehouseId]);
  const submitShelfMovement = async () => {
    try {
      setLoading(true);
      setError("");

      const payload = {
        shopId: assignedWarehouseId,
        productId: shelfForm.productId,
        quantityMoved: parseInt(shelfForm.quantityMoved),
        movementType: shelfForm.movementType,
        movedBy: user.fullName,
        notes: shelfForm.notes
      };

       

      const response = await fetch(`${API_BASE_URL}/ShopShelfMovement/move`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const text = await response.text();
         
        throw new Error(text || "Failed to move stock");
      }

      closeShelfModal();
      await fetchInventory();
      await loadShelfMovements();

    } catch (err) {
      setError("Failed to move stock to shelf: " + err.message);
    } finally {
      setLoading(false);
    }
  };
  

  const submitQA = async () => {
    try {
      setError("");
      setLoading(true);

      const payload = {
        receivingId: qaForm.receivingId,
        actualQuantityReceived: parseInt(qaForm.actualQuantityReceived),
        quantityInspected: parseInt(qaForm.quantityInspected),
        quantityAccepted: parseInt(qaForm.quantityAccepted),
        quantityDamaged: parseInt(qaForm.quantityDamaged),
        quantityExpired: parseInt(qaForm.quantityExpired),
        quantityDefective: parseInt(qaForm.quantityDefective),
        quantityIncorrect: parseInt(qaForm.quantityIncorrect),
        overallCondition: qaForm.overallCondition,
        rejectionReasons: qaForm.rejectionReasons,
        inspectedBy: user.fullName,
        notes: qaForm.notes,
        requiresFollowUp: qaForm.requiresFollowUp
      };

       

      const res = await fetch(`${API_BASE_URL}/ShopStockReceiving/process-qa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed processing QA");
      }

      closeModal();
      await fetchPendingQAs();
      await loadQualityChecks();

    } catch (err) {
      setError("Failed to process QA: " + err.message);
    } finally {
      setLoading(false);
    }
  };


  const fetchInventory = async () => {

      const response = await fetch(`${API_BASE_URL}/inventory/approved?warehouseId=${assignedWarehouseId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!response.ok) throw new Error("Failed to fetch inventory");

      const data = await response.json();

       

      setInventory(data);
  };

  const fetchPendingQAs = async () => {
      const response = await fetch(`${API_BASE_URL}/ShopStockReceiving/pending-qa?shopId=${assignedWarehouseId}`,
        {
          headers: {"Authorization": `Bearer ${token}`}
        }
      );

      if(!response.ok) throw new Error("Failed to fetch inventory");

      const data = await response.json();
       
      setPendingQAs(data); 
  }

  const [filters, setFilters] = useState({
    search: "",
  });

  const [pendingQAsFilters, setPendingQAsFilters] = useState({
    search: "",
    startDate: "",
    endDate: ""
  });

  const [QualityChecksFilters, setQualityChecksFilters] = useState({
    productName: "",
    purchaseOrderNumber: "",
    startDate: "",
    endDate: ""
  });

  const [shelfMovementFilters, setShelfMovemntFilters] = useState({
    productName: "",
    movementType: "",
    startDate: "",
    endDate: ""
  });

  const uniqueProductNames = useMemo(() => {
    const namesSet = new Set();
    (qualityChecks?.qualityChecks || []).forEach(p => namesSet.add(p.productName));
    return Array.from(namesSet).sort();
  }, [qualityChecks.qualityChecks]);
  
  const uniquePurchaseOrderNumbers = useMemo(() => {
    const namesSet = new Set();
    (qualityChecks?.qualityChecks || []).forEach(p => namesSet.add(p.purchaseOrderNumber));
    return Array.from(namesSet).sort();
  }, [qualityChecks.qualityChecks]);

  const uniqueShelfMovementProducts = useMemo(() => {
    const namesSet = new Set();
    (shelfMovements?.movements || []).forEach(p => namesSet.add(p.productName));
    return Array.from(namesSet).sort();
  }, [shelfMovements.movements]);
    
  const filteredInventory =inventory.filter(i =>
    (!filters.search ||
      i.productName.toLowerCase().includes(filters.search.toLowerCase()) 
    )
  );
    
  const filteredPendingQAs = (pendingQAs?.receivings || []).filter(p => 
    (!pendingQAsFilters.search ||
      p.productName.toLowerCase().includes(pendingQAsFilters.search.toLowerCase()) 
    ) &&

    (!pendingQAsFilters.startDate || new Date(p.receivedDate) >= new Date(pendingQAsFilters.startDate)) &&

    (!pendingQAsFilters.endDate || new Date(p.receivedDate) <= new Date(pendingQAsFilters.endDate))
  );

  const filteredQualityChecks = (qualityChecks?.qualityChecks || []).filter(q => 
    (!QualityChecksFilters.productName || q.id === QualityChecksFilters.productName || q.productName === QualityChecksFilters.productName) &&

    (!QualityChecksFilters.purchaseOrderNumber || q.id === QualityChecksFilters.purchaseOrderNumber || q.purchaseOrderNumber === QualityChecksFilters.purchaseOrderNumber) &&

    (!QualityChecksFilters.startDate || new Date(q.inspectedDate) >= new Date(QualityChecksFilters.startDate)) &&

    (!QualityChecksFilters.endDate || new Date(q.inspectedDate) <= new Date(QualityChecksFilters.endDate))
  );

  const filteredShelfMovements = (shelfMovements?.movements || []).filter(q => 
    (!shelfMovementFilters.productName || q.id === shelfMovementFilters.productName || q.productName === shelfMovementFilters.productName) &&

    (!shelfMovementFilters.movementType || q.id === shelfMovementFilters.movementType || q.movementType === shelfMovementFilters.movementType) &&

    (!shelfMovementFilters.startDate || new Date(q.movementDate) >= new Date(shelfMovementFilters.startDate)) &&

    (!shelfMovementFilters.endDate || new Date(q.movementDate) <= new Date(shelfMovementFilters.endDate))
  );

  // Inventory pagination
  const paginatedInventory = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredInventory.slice(start, start + pageSize);
  }, [filteredInventory, page, pageSize]);

  // Pending QAs pagination
  const paginatedPendingQAs = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredPendingQAs.slice(start, start + pageSize);
  }, [filteredPendingQAs, page, pageSize]);

  // Quality Checks pagination
  const paginatedQualityChecks = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredQualityChecks.slice(start, start + pageSize);
  }, [filteredQualityChecks, page, pageSize]);

  // Shelf Movements pagination
  const paginatedShelfMovements = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredShelfMovements.slice(start, start + pageSize);
  }, [filteredShelfMovements, page, pageSize]);

  useEffect(() => {
      setPage(1);
    }, [currentTab, filters, pendingQAsFilters, QualityChecksFilters, shelfMovementFilters]);

    const getLatestShelfUnits = (productId) => {
    if (!shelfMovements?.movements) return 0;

    const movement = shelfMovements.movements.find(
      (m) => m.productId === productId
    );

    return movement ? movement.remainingShelfUnits : 0;
  };
 
 
  return(
    <div className="stock-dashboard">
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>
      
      <div className="dashboard-content">
        <div className="header-section">
          <div className="header">
            <h2>STOCK TRACKER</h2>
            <div className="header-actions">
              <button>
                
              </button>
            </div>

            
          </div>
          <div className="assigned-warehouse">
            <p><strong>Warehouse: </strong>{getWarehouseName(assignedWarehouseId)}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="qa-tabs">
          <button
            className={currentTab === "inventory" ? "active-tab" : ""}
            onClick={() => setCurrentTab("inventory")}
          >
            Inventory
          </button>
          <button
            className={currentTab === "transfers" ? "active-tab" : ""}
            onClick={() => setCurrentTab("transfers")}
          >
            Pending Transfers {pendingQAs.count > 0 ? "!" : ""}
                  
                 
          </button>
          <button
            className={currentTab === "processed transfers" ? "active-tab" : ""}
            onClick={() => setCurrentTab("processed transfers")}
          >
            Processed Transfers 
          </button>
          <button
            className={currentTab === "shelf movements" ? "active-tab" : ""}
            onClick={() => setCurrentTab("shelf movements")}
          >
            Shelf Movements
          </button>
        </div>

        {currentTab === "inventory"&& (
          <div className="table-section">
            <div className="filters-section">
            <div className="form-group filter-group">
              <input
                type="text"
                placeholder=" "
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
              <label>Search Product...</label>
            </div>
          </div>
          <div className="cards-wrapper">
              {paginatedInventory.length > 0 ? (
                paginatedInventory.map(item => (
                  <ExpandableCard
                    key={item.id}
                    title={item.productName}
                    subtitle={`Total: ${item.units + getLatestShelfUnits(item.productId)}`}
                    actions={
                      <button
                        className="action-btn"
                        onClick={() => openShelfModal(item.productId)}
                      >
                        Move Stock
                      </button>
                    }
                  >
                    <div className="card-grid">
                      <div>
                        <label>Back Store: </label>
                        <span>{item.units}</span>
                      </div>
                      <div>
                        <label>On Shelves: </label>
                        <span>{getLatestShelfUnits(item.productId)}</span>
                      </div>
                      <div>
                        <label>Total: </label>
                        <span>{item.units + getLatestShelfUnits(item.productId)}</span>
                      </div>
                    </div>
                  </ExpandableCard>
                ))
              ) : (
                <p className="empty-state">No inventory found</p>
              )}
            </div>
            <div className="qa-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Previous
              </button>
              <span>Page {page}</span>
              <button 
                onClick={() => setPage((p) => p + 1)} 
                disabled={
                  currentTab === "inventory" && page >= Math.ceil(filteredInventory.length / pageSize) ||
                  currentTab === "transfers" && page >= Math.ceil(filteredPendingQAs.length / pageSize) ||
                  currentTab === "processed transfers" && page >= Math.ceil(filteredQualityChecks.length / pageSize) ||
                  currentTab === "shelf movements" && page >= Math.ceil(filteredShelfMovements.length / pageSize)
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        {currentTab === "transfers"&& (
          <div className="table-section">
            <div className="filters-section">
            <div className="form-group filter-group">
              <input
                type="text"
                placeholder=" "
                value={pendingQAsFilters.search}
                onChange={(e) => setPendingQAsFilters({ ...[pendingQAsFilters], search: e.target.value })}
              />
              <label>Search Product...</label>
            </div>
              <div className="form-group filter-group">
              <input
                type="date"
                value={pendingQAsFilters.startDate}
                onChange={(e) => setPendingQAsFilters({ ...pendingQAsFilters, startDate: e.target.value })}
              />
              <label>Start Date</label>
            </div>

            <div className="form-group filter-group">
              <input
                type="date"
                value={pendingQAsFilters.endDate}
                onChange={(e) => setPendingQAsFilters({ ...pendingQAsFilters, endDate: e.target.value })}
              />
              <label>End Date</label>
            </div>
          </div>
          <div className="cards-wrapper">
              {paginatedPendingQAs.length > 0 ? (
                paginatedPendingQAs.map(qa => (
                  <ExpandableCard
                    key={qa.id}
                    title={qa.productName}
                    subtitle={`Received: ${qa.expectedQuantity}`}
                    actions={
                      <button className="action-btn" onClick={() => openModal(qa.id)}>
                        Process Transfer
                      </button>
                    }
                  >
                    <div className="card-grid">
                      <div>
                        <label>Date Received: </label>
                        <span>{new Date(qa.receivedDate).toLocaleString()}</span>
                      </div>
                    </div>
                  </ExpandableCard>
                ))
              ) : (
                <p className="empty-state">No transfers yet</p>
              )}
            </div>
            <div className="qa-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Previous
              </button>
              <span>Page {page}</span>
              <button 
                onClick={() => setPage((p) => p + 1)} 
                disabled={
                  currentTab === "inventory" && page >= Math.ceil(filteredInventory.length / pageSize) ||
                  currentTab === "transfers" && page >= Math.ceil(filteredPendingQAs.length / pageSize) ||
                  currentTab === "processed transfers" && page >= Math.ceil(filteredQualityChecks.length / pageSize) ||
                  currentTab === "shelf movements" && page >= Math.ceil(filteredShelfMovements.length / pageSize)
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        {currentTab === "processed transfers"&& (
          <div className="table-section">
            <div className="filters-section">
            <div className="form-group filter-group">
              <select
                value={QualityChecksFilters.productName}
                onChange={(e) => setQualityChecksFilters({ ...QualityChecksFilters, productName: e.target.value })}
              >
                <option value="">All</option>
                {uniqueProductNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <label>Product</label>
            </div>
            <div className="form-group filter-group">
              <select
                value={QualityChecksFilters.purchaseOrderNumber}
                onChange={(e) => setQualityChecksFilters({ ...QualityChecksFilters, purchaseOrderNumber: e.target.value })}
              >
                <option value="">All</option>
                {uniquePurchaseOrderNumbers.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <label>Purchase Order Number</label>
            </div>
              <div className="form-group filter-group">
              <input
                type="date"
                value={QualityChecksFilters.startDate}
                onChange={(e) => setQualityChecksFilters({ ...QualityChecksFilters, startDate: e.target.value })}
              />
              <label>Start Date</label>
            </div>

            <div className="form-group filter-group">
              <input
                type="date"
                value={QualityChecksFilters.endDate}
                onChange={(e) => setQualityChecksFilters({ ...QualityChecksFilters, endDate: e.target.value })}
              />
              <label>End Date</label>
            </div>
          </div>
          <div className="cards-wrapper">
              {paginatedQualityChecks.length > 0 ? (
                paginatedQualityChecks.map(qc => (
                  <ExpandableCard
                    key={qc.id}
                    title={qc.productName}
                    subtitle={`PO: ${qc.purchaseOrderNumber}`}
                  >
                    <div className="card-grid">
                      <div><label>Expected: </label><span>{qc.expectedQuantity}</span></div>
                      <div><label>Received: </label><span>{qc.actualQuantityReceived}</span></div>
                      <div><label>Accepted: </label><span>{qc.quantityAccepted}</span></div>
                      <div><label>Rejected: </label><span>{qc.totalRejected}</span></div>
                      <div><label>Inspected By: </label><span>{qc.inspectedBy}</span></div>
                      <div><label>Date: </label><span>{new Date(qc.inspectedDate).toLocaleString()}</span></div>
                      <div><label>Notes: </label><span>{qc.notes || "—"}</span></div>
                      <div><label>Follow Up: </label><span>{qc.requiresFollowUp ? "Yes" : "No"}</span></div>
                    </div>
                  </ExpandableCard>
                ))
              ) : (
                <p className="empty-state">No processed transfers</p>
              )}
            </div>
            <div className="qa-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Previous
              </button>
              <span>Page {page}</span>
              <button 
                onClick={() => setPage((p) => p + 1)} 
                disabled={
                  currentTab === "inventory" && page >= Math.ceil(filteredInventory.length / pageSize) ||
                  currentTab === "transfers" && page >= Math.ceil(filteredPendingQAs.length / pageSize) ||
                  currentTab === "processed transfers" && page >= Math.ceil(filteredQualityChecks.length / pageSize) ||
                  currentTab === "shelf movements" && page >= Math.ceil(filteredShelfMovements.length / pageSize)
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        {currentTab === "shelf movements"&& (
          <div className="table-section">
            <div className="filters-section">
            <div className="form-group filter-group">
              <select
                value={shelfMovementFilters.productName}
                onChange={(e) => setShelfMovemntFilters({ ...shelfMovementFilters, productName: e.target.value })}
              >
                <option value="">All</option>
                {uniqueShelfMovementProducts.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <label>Product</label>
            </div>
            <div className="form-group filter-group">
              <select
                value={shelfMovementFilters.movementType}
                onChange={(e) => setShelfMovemntFilters({ ...shelfMovementFilters, movementType: e.target.value })}
              >
                <option value="">All</option>
                <option value="ToShelf">To shelf</option>
                <option value="FromShelf">From Shelf</option>
                <option value="Return">Return</option>
                <option value="Purchase">Purchased</option>
              </select>
              <label>Movement Type</label>
            </div>
              <div className="form-group filter-group">
              <input
                type="date"
                value={shelfMovementFilters.startDate}
                onChange={(e) => setShelfMovemntFilters({ ...shelfMovementFilters, startDate: e.target.value })}
              />
              <label>Start Date</label>
            </div>

            <div className="form-group filter-group">
              <input
                type="date"
                value={shelfMovementFilters.endDate}
                onChange={(e) => setShelfMovemntFilters({ ...shelfMovementFilters, endDate: e.target.value })}
              />
              <label>End Date</label>
            </div>
          </div>
          <div className="cards-wrapper">
              {paginatedShelfMovements.length > 0 ? (
                paginatedShelfMovements.map(sm => (
                  <ExpandableCard
                    key={sm.id}
                    title={sm.productName}
                    subtitle={sm.movementType}
                  >
                    <div className="card-grid">
                      <div><label>Quantity Moved: </label><span>{sm.quantityMoved}</span></div>
                      <div><label>Remaining Inventory: </label><span>{sm.remainingInventory}</span></div>
                      <div><label>On Shelves: </label><span>{sm.remainingShelfUnits}</span></div>
                      <div><label>Date: </label><span>{new Date(sm.movementDate).toLocaleString()}</span></div>
                      <div><label>Moved By: </label><span>{sm.movedBy}</span></div>
                    </div>
                  </ExpandableCard>
                ))
              ) : (
                <p className="empty-state">No shelf movements</p>
              )}
            </div>
            <div className="qa-pagination">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                Previous
              </button>
              <span>Page {page}</span>
              <button 
                onClick={() => setPage((p) => p + 1)} 
                disabled={
                  currentTab === "inventory" && page >= Math.ceil(filteredInventory.length / pageSize) ||
                  currentTab === "transfers" && page >= Math.ceil(filteredPendingQAs.length / pageSize) ||
                  currentTab === "processed transfers" && page >= Math.ceil(filteredQualityChecks.length / pageSize) ||
                  currentTab === "shelf movements" && page >= Math.ceil(filteredShelfMovements.length / pageSize)
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* QA Modal */}
        {showModal && (
          <div className="qa-modal-overlay">
            <div className="qa-modal">
              <div className="modal-form">
                <h2>Process Stock QA</h2>
                <div className="quantities-section">
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.actualQuantityReceived}
                      onChange={(e) => setQaForm({ ...qaForm, actualQuantityReceived: e.target.value })}
                    />
                    <label>Actual Quantity received</label>
                  </div>
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.quantityInspected}
                      onChange={(e) => setQaForm({ ...qaForm, quantityInspected: e.target.value })}
                    />
                    <label>Quantity Inspected</label>
                  </div>
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.quantityAccepted}
                      onChange={(e) => setQaForm({ ...qaForm, quantityAccepted: e.target.value })}
                    />
                    <label>Quantity Accepeted</label>
                  </div>
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.quantityDamaged}
                      onChange={(e) => setQaForm({ ...qaForm, quantityDamaged: e.target.value })}
                    />
                    <label>Quantity Damaged</label>
                  </div>
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.quantityExpired}
                      onChange={(e) => setQaForm({ ...qaForm, quantityExpired: e.target.value })}
                    />
                    <label>Quantity Expired</label>
                  </div>
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.quantityDefective}
                      onChange={(e) => setQaForm({ ...qaForm, quantityDefective: e.target.value })}
                    />
                    <label>Quantity Defective</label>
                  </div>
                  <div className='form-group'>
                    <input
                      type="number"
                      placeholder=" "
                      value={qaForm.quantityIncorrect}
                      onChange={(e) => setQaForm({ ...qaForm, quantityIncorrect: e.target.value })}
                    />
                    <label>Quantity Incorrect</label>
                  </div>
                </div>
                
                <label>Overall Condition:</label>
                <div className="compact-radio-group">
                  
                  <label className="compact-radio">
                    <input
                      type="radio"
                      name="overallCondition"
                      value="Good"
                      checked={qaForm.overallCondition === "Good"}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, overallCondition: e.target.value })
                      }
                    />
                    Good
                  </label>

                  <label className="compact-radio">
                    <input
                      type="radio"
                      name="overallCondition"
                      value="Fair"
                      checked={qaForm.overallCondition === "Fair"}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, overallCondition: e.target.value })
                      }
                    />
                    Fair
                  </label>

                  <label className="compact-radio">
                    <input
                      type="radio"
                      name="overallCondition"
                      value="Poor"
                      checked={qaForm.overallCondition === "Poor"}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, overallCondition: e.target.value })
                      }
                    />
                    Poor
                  </label>
                </div>
                <div className='form-group'>
                  <input
                    placeholder=" "
                    value={qaForm.rejectionReasons}
                    onChange={(e) => setQaForm({ ...qaForm, rejectionReasons: e.target.value })}
                  />
                  <label>Rejection Reasons</label>
                </div>
                <div className='form-group'>
                  <textarea
                    placeholder=" "
                    value={qaForm.notes}
                    onChange={(e) => setQaForm({ ...qaForm, notes: e.target.value })}
                  />
                  <label>Notes (optional)</label>
                </div>
                <div className="checkbox-field">
                  <label className="compact-checkbox">
                    <input
                      type="checkbox"
                      checked={qaForm.requiresFollowUp}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, requiresFollowUp: e.target.checked })
                      }
                    />
                    <span>Requires follow up</span>
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={closeModal}>Cancel</button>
                <button className="submit-btn" onClick={submitQA}>Submit QA</button>
              </div>
            </div>
          </div>
        )}

        {showShelfModal && (
          <div className="qa-modal-overlay">
            <div className="shelf-modal">
              <h2>Move Stock</h2>

              <div className="modal-form">
                <input
                  type="number"
                  placeholder="Quantity to move"
                  value={shelfForm.quantityMoved}
                  onChange={(e) =>
                    setShelfForm({ ...shelfForm, quantityMoved: e.target.value })
                  }
                />
                <select 
                  value={shelfForm.movementType}
                  onChange={(e) => setShelfForm({ ...shelfForm, movementType: e.target.value})}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    outline: 'none',
                    minWidth: '150px'
                  }}
                >
                  <option value="">Select Movement type...</option>
                  <option value="ToShelf">To shelf</option>
                  <option value="FromShelf">Back to inventory</option>
                  <option value="Return">Remove from shelf (Expired/damaged)</option>
                  <option value="Purchase">Purchase</option>
                </select>
                <textarea
                  placeholder="Notes (optional)"
                  value={shelfForm.notes}
                  onChange={(e) =>
                    setShelfForm({ ...shelfForm, notes: e.target.value })
                  }
                />
              </div>

              <div className="modal-actions">
                <button className="cancel-btn" onClick={closeShelfModal}>
                  Cancel
                </button>
                <button className="submit-btn" onClick={submitShelfMovement}>
                  Move
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}